import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LogOut, User, Plus, LayoutDashboard, ShieldCheck, Menu, X, HelpCircle, Search, Briefcase, Smartphone } from "lucide-react";

export function Logo({ light = false }) {
  return (
    <Link to="/" className="flex items-center gap-2 group" data-testid="lokaso-logo">
      <span className="lokaso-flag-stripe h-7"><span /><span /><span /></span>
      <span
        className={`font-display text-2xl font-extrabold tracking-tight ${light ? "text-lokaso-sand" : "text-lokaso-dark"}`}
      >
        LOKASO
      </span>
    </Link>
  );
}

export default function Header() {
  const { user, logout } = useAuth() || {};
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  return (
    <header className="sticky top-0 z-40 w-full bg-[#1A5C35] text-lokaso-sand border-b border-black/10 shadow-sm" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo-link">
            <span className="lokaso-flag-stripe h-7"><span /><span /><span /></span>
            <span className="font-display text-2xl font-extrabold tracking-tight text-lokaso-sand">LOKASO</span>
          </Link>
        </div>

        <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
          <Link to="/search" className="hover:text-lokaso-gold transition-colors" data-testid="nav-explore">Explorer</Link>
          <Link to="/business/register" className="hover:text-lokaso-gold transition-colors" data-testid="nav-add-business">
            Inscrire mon entreprise
          </Link>
          <Link to="/faq" className="hover:text-lokaso-gold transition-colors" data-testid="nav-faq">FAQ</Link>
          <Link to="/app" className="inline-flex items-center gap-1.5 hover:text-lokaso-gold transition-colors" data-testid="nav-install-app">
            <Smartphone className="w-4 h-4" />
            <span>Installer l'appli</span>
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Link
                to="/dashboard"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-medium"
                data-testid="header-dashboard-link"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span>Tableau de bord</span>
              </Link>
              {user.is_admin && (
                <Link
                  to="/admin"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-lokaso-gold text-lokaso-dark hover:brightness-95 text-sm font-semibold"
                  data-testid="header-admin-link"
                >
                  <ShieldCheck className="w-4 h-4" />
                  <span>Admin</span>
                </Link>
              )}
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-sm"
                data-testid="header-logout-btn"
                title="Déconnexion"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-sm font-medium"
                data-testid="header-login-link"
              >
                <User className="w-4 h-4" />
                <span>Se connecter</span>
              </Link>
              <Link
                to="/business/register"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-lokaso-gold text-lokaso-dark hover:brightness-95 text-sm font-semibold"
                data-testid="header-add-business-cta"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden xs:inline">Ajouter</span>
              </Link>
            </>
          )}

          {/* Hamburger - mobile only */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20"
            aria-label="Menu"
            aria-expanded={mobileOpen}
            data-testid="header-mobile-menu-btn"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div
          className="md:hidden border-t border-white/10 bg-[#1A5C35]"
          data-testid="header-mobile-menu"
        >
          <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col text-sm font-medium">
            <Link
              to="/search"
              onClick={closeMobile}
              className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/10"
              data-testid="mobile-nav-explore"
            >
              <Search className="w-4 h-4 text-lokaso-gold" /> Explorer
            </Link>
            <Link
              to="/business/register"
              onClick={closeMobile}
              className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/10"
              data-testid="mobile-nav-add-business"
            >
              <Briefcase className="w-4 h-4 text-lokaso-gold" /> Inscrire mon entreprise
            </Link>
            <Link
              to="/faq"
              onClick={closeMobile}
              className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/10"
              data-testid="mobile-nav-faq"
            >
              <HelpCircle className="w-4 h-4 text-lokaso-gold" /> FAQ
            </Link>
            <Link
              to="/app"
              onClick={closeMobile}
              className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/10"
              data-testid="mobile-nav-install-app"
            >
              <Smartphone className="w-4 h-4 text-lokaso-gold" /> Installer l'appli
            </Link>

            <div className="border-t border-white/10 my-2" />

            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={closeMobile}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/10"
                  data-testid="mobile-nav-dashboard"
                >
                  <LayoutDashboard className="w-4 h-4 text-lokaso-gold" /> Tableau de bord
                </Link>
                {user.is_admin && (
                  <Link
                    to="/admin"
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/10"
                    data-testid="mobile-nav-admin"
                  >
                    <ShieldCheck className="w-4 h-4 text-lokaso-gold" /> Admin
                  </Link>
                )}
              </>
            ) : (
              <Link
                to="/login"
                onClick={closeMobile}
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-white/10"
                data-testid="mobile-nav-login"
              >
                <User className="w-4 h-4 text-lokaso-gold" /> Se connecter
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
